import logo from 'assets/header-prevencor-xx.png';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import QRCode from 'qrcode';
import fetchImageBase64 from 'utils/helpers';
import { pdfMakeGetBlob } from 'utils/pdfBlob';
import { buildValidacaoQrCodeUrl } from 'utils/validacaoQrCode';
import { ehReceitaLivre } from './receitaLivre';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/** Receita impressa: título, texto do médico, logo timbrado e espaço de assinatura. */
async function generateReceitaLivrePdf({ receita }) {
  const logoBase64 = await fetchImageBase64(logo);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 24, 40, 100],
    content: [
      {
        image: logoBase64,
        width: 550,
        alignment: 'center',
        margin: [0, 0, 0, 16],
      },
      {
        text: 'RECEITA',
        style: 'header',
        alignment: 'center',
        margin: [0, 0, 0, 24],
      },
      {
        text: receita?.TEXTO || '',
        margin: [30, 0, 30, 0],
        fontSize: 12,
      },
    ],
    footer: () => ({
      margin: [40, 8, 40, 0],
      stack: [
        {
          text: '________________________________',
          alignment: 'center',
          margin: [0, 0, 0, 2],
        },
        {
          text: 'Assinatura e carimbo',
          alignment: 'center',
          fontSize: 10,
        },
      ],
    }),
    styles: {
      header: { fontSize: 16, bold: true },
    },
    defaultStyle: { fontSize: 12 },
  };

  return pdfMakeGetBlob(pdfMake.createPdf(docDefinition));
}

const BORDAS_TABELA_ANVISA = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => 'black',
  vLineColor: () => 'black',
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 2,
  paddingBottom: () => 2,
};

function buildQuadroEmitente(receita) {
  return {
    table: {
      widths: ['*', 150],
      body: [
        [
          {
            text: 'IDENTIFICAÇÃO DO EMITENTE',
            colSpan: 2,
            fillColor: '#000000',
            color: '#ffffff',
            bold: true,
            alignment: 'center',
            fontSize: 9,
            margin: [0, 1, 0, 1],
          },
          {},
        ],
        [
          {
            text: `Médico: Dr. ${receita.NOMEMED}`,
            fontSize: 8.5,
            border: [true, true, false, true],
          },
          {
            text: `CRM: ${receita.CRMMED}`,
            fontSize: 8.5,
            border: [false, true, true, true],
          },
        ],
        [
          {
            text: 'Endereço: RUA CLÁUDIO MANOEL DA COSTA, 56 - BAIRRO MARIA IZABEL - CEP 17515-460',
            colSpan: 2,
            fontSize: 8.5,
          },
          {},
        ],
        [
          {
            text: 'Cidade: MARÍLIA - UF: SP',
            fontSize: 8.5,
            border: [true, true, false, true],
          },
          {
            text: 'Telefone: (14) 3402-2700',
            fontSize: 8.5,
            border: [false, true, true, true],
          },
        ],
      ],
    },
    layout: BORDAS_TABELA_ANVISA,
    margin: [30, 0, 30, 0],
  };
}

function buildBlocoAssinaturaDigital({ receita, qrCodeReceita }) {
  return {
    columns: [
      {
        width: 65,
        image: qrCodeReceita,
        fit: [58, 58],
        alignment: 'center',
      },
      {
        width: '*',
        margin: [6, 2, 0, 0],
        stack: [
          {
            text: `Documento assinado eletronicamente — Médico: Dr. ${receita.NOMEMED} - CRM: ${receita.CRMMED}`,
            bold: true,
            fontSize: 9,
            margin: [0, 0, 0, 2],
          },
          {
            text: `Autenticação: ${md5(JSON.stringify(receita))}`,
            italics: true,
            fontSize: 8,
            margin: [0, 0, 0, 2],
          },
          {
            text: `VALIDADOR DE RECEITA DIGITAL - PREVENCOR | CÓD. RECEITA: ${receita.CODRECEITA} - CÓD. PACIENTE: ${receita.CODPAC}`,
            bold: true,
            fontSize: 8,
            margin: [0, 0, 0, 2],
          },
          {
            text: 'Aponte a câmera do celular para o QRCode e verifique a validade deste documento.',
            italics: true,
            fontSize: 7.5,
          },
        ],
      },
    ],
    margin: [0, 0, 0, 6],
  };
}

function buildLinhaCreditosPaginacao(currentPage, pageCount) {
  return {
    text: `Prevencor Marília — Página ${currentPage} de ${pageCount}`,
    alignment: 'center',
    fontSize: 7.5,
  };
}

function buildFooterReceitaNormal({ receita, qrCodeReceita, currentPage, pageCount }) {
  return {
    margin: [30, 8, 30, 0],
    fontSize: 8,
    stack: [
      buildBlocoAssinaturaDigital({ receita, qrCodeReceita }),
      buildLinhaCreditosPaginacao(currentPage, pageCount),
    ],
  };
}

function buildFooterReceitaControlada({ receita, qrCodeReceita, currentPage, pageCount }) {
  return {
    margin: [30, 4, 30, 0],
    fontSize: 8,
    stack: [
      // 1. Bloco Compacto de Assinatura Digital + QR Code (substitui Data / Assinatura física)
      buildBlocoAssinaturaDigital({ receita, qrCodeReceita }),

      // 2. Quadros Anvisa Lado a Lado: Identificação do Comprador e Identificação do Fornecedor
      {
        columns: [
          {
            width: '*',
            table: {
              widths: ['*', 95],
              body: [
                [
                  {
                    text: 'IDENTIFICAÇÃO DO COMPRADOR',
                    colSpan: 2,
                    fillColor: '#000000',
                    color: '#ffffff',
                    bold: true,
                    alignment: 'center',
                    fontSize: 8.5,
                    margin: [0, 1, 0, 1],
                  },
                  {},
                ],
                [{ text: 'Nome Completo:', colSpan: 2, fontSize: 7.5 }, {}],
                [{ text: ' ', colSpan: 2, fontSize: 7.5 }, {}],
                [
                  { text: 'Identidade:', fontSize: 7.5, border: [true, true, false, true] },
                  { text: 'Órg. Emissor:', fontSize: 7.5, border: [false, true, true, true] },
                ],
                [{ text: 'Endereço:', colSpan: 2, fontSize: 7.5 }, {}],
                [{ text: ' ', colSpan: 2, fontSize: 7.5 }, {}],
                [
                  { text: 'Cidade:', fontSize: 7.5, border: [true, true, false, true] },
                  { text: 'UF:', fontSize: 7.5, border: [false, true, true, true] },
                ],
                [{ text: 'Telefone: (      )', colSpan: 2, fontSize: 7.5 }, {}],
              ],
            },
            layout: BORDAS_TABELA_ANVISA,
          },
          {
            width: '*',
            table: {
              widths: ['*'],
              heights: [undefined, 96],
              body: [
                [
                  {
                    text: 'IDENTIFICAÇÃO DO FORNECEDOR',
                    fillColor: '#000000',
                    color: '#ffffff',
                    bold: true,
                    alignment: 'center',
                    fontSize: 8.5,
                    margin: [0, 1, 0, 1],
                  },
                ],
                [
                  {
                    stack: [
                      { text: ' ', margin: [0, 0, 0, 62] },
                      {
                        columns: [
                          {
                            width: '*',
                            stack: [
                              {
                                text: '_____________________________',
                                alignment: 'center',
                                fontSize: 7.5,
                              },
                              {
                                text: 'Assinatura do Farmacêutico',
                                alignment: 'center',
                                fontSize: 7.5,
                                margin: [0, 1, 0, 0],
                              },
                            ],
                          },
                          {
                            width: 90,
                            stack: [
                              {
                                text: '____/____/______',
                                alignment: 'center',
                                fontSize: 7.5,
                              },
                              {
                                text: 'Data',
                                alignment: 'center',
                                fontSize: 7.5,
                                margin: [0, 1, 0, 0],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              ],
            },
            layout: BORDAS_TABELA_ANVISA,
          },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 4],
      },

      // 3. Créditos + Paginação
      buildLinhaCreditosPaginacao(currentPage, pageCount),
    ],
  };
}

// Função principal para gerar PDF da Receita (normal, controlada ou livre)
export async function generateReceitaPdf({ receita } = {}) {
  if (ehReceitaLivre(receita)) {
    return generateReceitaLivrePdf({ receita });
  }

  const logoBase64 = await fetchImageBase64(logo);
  const qrCodeReceita = await QRCode.toDataURL(
    buildValidacaoQrCodeUrl({
      tipo: 'receita',
      codDocumento: receita?.CODRECEITA,
      codPaciente: receita?.CODPAC,
    })
  );

  // Receita Controlada
  if (receita?.TIPORECEITA === 'Controlada') {
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 20, 40, 235],
      content: [
        // Cabeçalho
        {
          image: logoBase64,
          width: 550,
          alignment: 'center',
          margin: [0, 0, 0, 20],
        },
        {
          text: '1ª VIA (Farmácia) - RECEITUÁRIO DE CONTROLE ESPECIAL',
          style: 'header',
          alignment: 'center',
          margin: [0, 0, 0, 10],
        },
        buildQuadroEmitente(receita),

        // Medicamentos
        { text: receita.TEXTO, margin: [30, 20, 30, 10], fontSize: 11 },

        { text: '', pageBreak: 'before' },

        // Segunda via (idêntica à primeira)
        {
          image: logoBase64,
          width: 550,
          alignment: 'center',
          margin: [0, 0, 0, 20],
        },
        {
          text: '2ª VIA (Paciente) - RECEITUÁRIO DE CONTROLE ESPECIAL',
          style: 'header',
          alignment: 'center',
          margin: [0, 0, 0, 10],
        },
        buildQuadroEmitente(receita),

        {
          text: receita.TEXTO,
          margin: [30, 20, 30, 10],
          fontSize: 11,
        },
      ],
      styles: {
        header: { fontSize: 14, bold: true },
        footer: { fontSize: 10, italics: true },
      },
      footer: (currentPage, pageCount) =>
        buildFooterReceitaControlada({
          receita,
          qrCodeReceita,
          currentPage,
          pageCount,
        }),

      defaultStyle: {
        fontSize: 11,
      },
    };
    return pdfMakeGetBlob(pdfMake.createPdf(docDefinition));
  }

  // Receita Normal
  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 20, 40, 100],
    content: [
      {
        image: logoBase64,
        width: 550,
        alignment: 'center',
        margin: [0, 0, 0, 20],
      },
      {
        text: 'RECEITUÁRIO DO PACIENTE',
        style: 'header',
        alignment: 'center',
      },
      {
        text: receita.TEXTO,
        margin: [30, 40, 30, 10],
        fontSize: 12,
      },
    ],
    styles: {
      header: { fontSize: 16, bold: true },
      subheader: { fontSize: 12, bold: true },
      footer: { italics: true },
    },
    footer: (currentPage, pageCount) =>
      buildFooterReceitaNormal({
        receita,
        qrCodeReceita,
        currentPage,
        pageCount,
      }),
    defaultStyle: { fontSize: 11 },
  };
  return pdfMakeGetBlob(pdfMake.createPdf(docDefinition));
}
